import React from 'react';
import { US_STATES, COUNTRIES } from '../constants/states';
import { API_BASE_URL } from '../config/api';
import CountyDropdown from './CountyDropdown';
import AgencyDropdown from './AgencyDropdown';

const ImmigrantForm = ({ formData, handleInputChange, handleSubmit, loading, isEditing, onCancelEdit, photoFileInputRef, fieldErrors = {} }) => {

  // Helper function to convert text to title case
  const toTitleCase = (str) => {
    if (!str) return str;
    return str.toLowerCase().replace(/\b\w/g, (char) => char.toUpperCase());
  };

  // Helper function to handle title case input changes
  const handleTitleCaseInputChange = (e) => {
    const { name, value } = e.target;
    const titleCaseValue = toTitleCase(value);
    const syntheticEvent = {
      target: {
        name: name,
        value: titleCaseValue,
        type: 'text'
      }
    };
    handleInputChange(syntheticEvent);
  };

  // Auto-populate ORI and Agency Name removed - users must enter manually

  // Function to get input class with error styling
  const getInputClass = (fieldName) => {
    const baseClass = 'modern-input';
    return fieldErrors[fieldName] ? `${baseClass} field-error` : baseClass;
  };

  // Add CSS for field errors
  const errorStyle = {
    borderColor: '#dc3545',
    boxShadow: '0 0 0 0.2rem rgba(220, 53, 69, 0.25)',
    backgroundColor: '#fff5f5'
  };
  // Prevent automatic scrolling when date inputs are focused
  const handleDateFocus = (e) => {
    // Store the current scroll position of all scrollable containers
    const scrollContainer = e.target.closest('.sidePanel');
    const bodyScrollTop = document.body.scrollTop || document.documentElement.scrollTop;

    if (scrollContainer) {
      const currentScrollTop = scrollContainer.scrollTop;

      // Use multiple requestAnimationFrame calls to ensure this runs after all browser behaviors
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          scrollContainer.scrollTop = currentScrollTop;
          document.body.scrollTop = bodyScrollTop;
          document.documentElement.scrollTop = bodyScrollTop;
        });
      });
    }
  };

  // Simple date input handler - let HTML5 min/max handle year validation
  const handleDateInputChange = (e) => {
    // Just pass through to the original handler
    // HTML5 min/max attributes will handle year validation
    handleInputChange(e);
  };

  // Handle click events on date inputs to prevent scrolling
  const handleDateClick = (e) => {
    const scrollContainer = e.target.closest('.sidePanel');
    const bodyScrollTop = document.body.scrollTop || document.documentElement.scrollTop;

    if (scrollContainer) {
      const currentScrollTop = scrollContainer.scrollTop;

      // Prevent scrolling when date picker opens
      setTimeout(() => {
        scrollContainer.scrollTop = currentScrollTop;
        document.body.scrollTop = bodyScrollTop;
        document.documentElement.scrollTop = bodyScrollTop;
      }, 0);

      // Additional check after a longer delay for slower browsers
      setTimeout(() => {
        scrollContainer.scrollTop = currentScrollTop;
        document.body.scrollTop = bodyScrollTop;
        document.documentElement.scrollTop = bodyScrollTop;
      }, 100);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="dataEntryForm">
      {/* Immigration Record Data Title */}
      <h2 style={{
        color: 'var(--primary-color)',
        fontSize: '20px',
        marginBottom: '20px',
        textAlign: 'left',
        borderBottom: '2px solid var(--primary-color)',
        paddingBottom: '10px'
      }}>
        Required FDLE Submission Data
      </h2>

      {/* 1. Date of Contact */}
      <h3>1. Date of Contact</h3>
      <div className="formGroup">
        <label>Date of Contact <span style={{color: '#dc3545'}}>*</span></label>
        <input
          type="date"
          name="contactDate"
          value={formData.contactDate}
          onChange={handleDateInputChange}
          onFocus={handleDateFocus}
          onClick={handleDateClick}
          className="modern-input"
          min="1000-01-01"
          max="9999-12-31"
          required
          title="Required by FDLE - Date when contact with suspected alien occurred"
        />
      </div>

      {/* 2. Contacting Agency */}
      <h3>2. Contacting Agency</h3>
      <div className="formRow">
        <div className="formGroup">
          <label>ORI (Originating Agency Identifier) <span style={{color: '#dc3545'}}>*</span></label>
          <input
            type="text"
            name="ori"
            value={formData.ori}
            onChange={(e) => {
              // Only allow digits, any length
              const value = e.target.value.replace(/[^0-9]/g, '');
              const syntheticEvent = {
                target: {
                  name: 'ori',
                  value: value,
                  type: 'text'
                }
              };
              handleInputChange(syntheticEvent);
            }}
            onKeyDown={(e) => {
              // Prevent non-numeric characters from being typed
              if (!/[0-9]/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'Tab' && e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') {
                e.preventDefault();
              }
            }}
            className={getInputClass('ori')}
            style={fieldErrors.ori ? errorStyle : {}}
            required
            title="Required by FDLE - Agency ORI Number (numbers only)"
            placeholder="Numbers only, no letters - Do not put FL"
          />
          {fieldErrors.ori && (
            <div style={{fontSize: '12px', color: '#dc3545', marginTop: '4px'}}>
              This field is required for FDLE submission
            </div>
          )}
          <small style={{ color: '#666', fontSize: '12px', fontStyle: 'italic', display: 'block', marginTop: '4px' }}>
            Enter numbers only, do not include "FL" prefix
          </small>
        </div>
        <div className="formGroup">
          <label>Agency Name <span style={{color: '#dc3545'}}>*</span></label>
          <AgencyDropdown
            name="agencyName"
            value={formData.agencyName}
            onChange={handleInputChange}
            className="modern-input"
            required
          />
        </div>
      </div>

      <div className="formRow">
        <div className="formGroup">
          <label>Submitter's First Name <span style={{color: '#dc3545'}}>*</span></label>
          <input
            type="text"
            name="officerFirstName"
            value={formData.officerFirstName}
            onChange={handleTitleCaseInputChange}
            className={getInputClass('officerFirstName')}
            style={fieldErrors.officerFirstName ? errorStyle : {}}
            required
          />
          {fieldErrors.officerFirstName && (
            <div style={{fontSize: '12px', color: '#dc3545', marginTop: '4px'}}>
              Submitter first name is required for FDLE submission
            </div>
          )}
        </div>
        <div className="formGroup">
          <label>Submitter's Last Name <span style={{color: '#dc3545'}}>*</span></label>
          <input
            type="text"
            name="officerLastName"
            value={formData.officerLastName}
            onChange={handleTitleCaseInputChange}
            className={getInputClass('officerLastName')}
            style={fieldErrors.officerLastName ? errorStyle : {}}
            required
          />
          {fieldErrors.officerLastName && (
            <div style={{fontSize: '12px', color: '#dc3545', marginTop: '4px'}}>
              Submitter last name is required for FDLE submission
            </div>
          )}
        </div>
      </div>
      <div className="formGroup">
        <label>Badge or ID# <span style={{color: '#dc3545'}}>*</span></label>
        <input
          type="text"
          name="badgeOrId"
          value={formData.badgeOrId}
          onChange={handleTitleCaseInputChange}
          className="modern-input"
          required
        />
      </div>
      <div className="formRow">
        <div className="formGroup">
          <label>Submitter Title/Rank <span style={{color: '#dc3545'}}>*</span></label>
          <input
            type="text"
            name="officerTitle"
            value={formData.officerTitle}
            onChange={handleTitleCaseInputChange}
            className="modern-input"
            placeholder="e.g., Detective, Sergeant, Officer"
            required
          />
        </div>
        <div className="formGroup">
          <label>Submitter Phone <span style={{color: '#dc3545'}}>*</span></label>
          <input
            type="tel"
            name="officerPhone"
            value={formData.officerPhone}
            onChange={(e) => {
              // Format phone number as XXX-XXX-XXXX
              let value = e.target.value.replace(/\D/g, ''); // Remove non-digits
              // Limit to 10 digits
              value = value.slice(0, 10);
              // Format as XXX-XXX-XXXX
              if (value.length >= 6) {
                value = value.slice(0, 3) + '-' + value.slice(3, 6) + '-' + value.slice(6, 10);
              } else if (value.length >= 3) {
                value = value.slice(0, 3) + '-' + value.slice(3);
              }

              const syntheticEvent = {
                target: {
                  name: 'officerPhone',
                  value: value,
                  type: 'tel'
                }
              };
              handleInputChange(syntheticEvent);
            }}
            className={getInputClass('officerPhone')}
            style={fieldErrors.officerPhone ? errorStyle : {}}
            placeholder="555-555-5555"
            title="Enter valid 10-digit phone number XXX-XXX-XXXX"
            required
          />
          {fieldErrors.officerPhone && (
            <div style={{fontSize: '12px', color: '#dc3545', marginTop: '4px'}}>
              Submitter phone number is required for FDLE submission
            </div>
          )}
          <small style={{ color: '#666', fontSize: '12px', fontStyle: 'italic', display: 'block', marginTop: '4px' }}>
            Enter valid 10-digit phone number XXX-XXX-XXXX
          </small>
        </div>
      </div>
      <div className="formGroup">
        <label>Submitter Email <span style={{color: '#dc3545'}}>*</span></label>
        <input
          type="email"
          name="officerEmail"
          value={formData.officerEmail}
          onChange={handleInputChange}
          onBlur={(e) => {
            // Validate email format on blur for immediate feedback
            const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
            const isValid = emailRegex.test(e.target.value.trim());
            if (!isValid && e.target.value.trim() !== '') {
              e.target.setCustomValidity('Please enter a valid email address (e.g., submitter@agency.gov)');
            } else {
              e.target.setCustomValidity('');
            }
          }}
          className={getInputClass('officerEmail')}
          style={{
            ...fieldErrors.officerEmail ? errorStyle : {},
            borderColor: (() => {
              if (fieldErrors.officerEmail) return '#dc3545';
              if (!formData.officerEmail || formData.officerEmail.trim() === '') return '#ced4da';
              const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
              const isValid = emailRegex.test(formData.officerEmail.trim());
              return isValid ? '#28a745' : '#dc3545'; // Green for valid, red for invalid
            })()
          }}
          placeholder="submitter@agency.gov"
          title="Enter a valid email address (e.g., submitter@agency.gov)"
          required
        />
        {fieldErrors.officerEmail && (
          <div style={{fontSize: '12px', color: '#dc3545', marginTop: '4px'}}>
            Submitter email address is required for FDLE submission
          </div>
        )}
        {formData.officerEmail && formData.officerEmail.trim() !== '' && (() => {
          const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
          const isValid = emailRegex.test(formData.officerEmail.trim());
          if (!isValid) {
            return (
              <div style={{fontSize: '12px', color: '#dc3545', marginTop: '4px'}}>
                Please enter a valid email address (e.g., submitter@agency.gov)
              </div>
            );
          }
          return null;
        })()}
        <small style={{ color: '#666', fontSize: '12px', fontStyle: 'italic', display: 'block', marginTop: '4px' }}>
          Enter a valid email address (e.g., submitter@agency.gov)
        </small>
      </div>

      {/* 3-5. Location of Contact */}
      <h3>3-5. Location of Alien Contact</h3>
      <div className="formRow">
        <div className="formGroup">
          <label>County of Alien Contact <span style={{color: '#dc3545'}}>*</span></label>
          <CountyDropdown
            name="contactCounty"
            value={formData.contactCounty}
            onChange={handleInputChange}
            className="modern-input"
            required
          />
        </div>
        <div className="formGroup">
          <label>City of Alien Contact <span style={{color: '#dc3545'}}>*</span></label>
          <select
            name="contactCity"
            value={formData.contactCity}
            onChange={handleInputChange}
            className="modern-input"
            required
          >
            <option value="">Select City</option>
            <option value="Alachua">Alachua</option>
            <option value="Alford">Alford</option>
            <option value="Altamonte Springs">Altamonte Springs</option>
            <option value="Altha">Altha</option>
            <option value="Anna Maria">Anna Maria</option>
            <option value="Apalachicola">Apalachicola</option>
            <option value="Apopka">Apopka</option>
            <option value="Arcadia">Arcadia</option>
            <option value="Archer">Archer</option>
            <option value="Astatula">Astatula</option>
            <option value="Atlantic Beach">Atlantic Beach</option>
            <option value="Atlantis">Atlantis</option>
            <option value="Auburndale">Auburndale</option>
            <option value="Aventura">Aventura</option>
            <option value="Avon Park">Avon Park</option>
            <option value="Bal Harbour">Bal Harbour</option>
            <option value="Baldwin">Baldwin</option>
            <option value="Bartow">Bartow</option>
            <option value="Bascom">Bascom</option>
            <option value="Bay Harbor Islands">Bay Harbor Islands</option>
            <option value="Bay Lake">Bay Lake</option>
            <option value="Bell">Bell</option>
            <option value="Belle Glade">Belle Glade</option>
            <option value="Belle Isle">Belle Isle</option>
            <option value="Belleair">Belleair</option>
            <option value="Belleair Beach">Belleair Beach</option>
            <option value="Belleair Bluffs">Belleair Bluffs</option>
            <option value="Belleair Shore">Belleair Shore</option>
            <option value="Belleview">Belleview</option>
            <option value="Beverly Beach">Beverly Beach</option>
            <option value="Biscayne Park">Biscayne Park</option>
            <option value="Blountstown">Blountstown</option>
            <option value="Boca Raton">Boca Raton</option>
            <option value="Bonifay">Bonifay</option>
            <option value="Bonita Springs">Bonita Springs</option>
            <option value="Bowling Green">Bowling Green</option>
            <option value="Boynton Beach">Boynton Beach</option>
            <option value="Bradenton">Bradenton</option>
            <option value="Bradenton Beach">Bradenton Beach</option>
            <option value="Branford">Branford</option>
            <option value="Briny Breezes">Briny Breezes</option>
            <option value="Bristol">Bristol</option>
            <option value="Bronson">Bronson</option>
            <option value="Brooker">Brooker</option>
            <option value="Brooksville">Brooksville</option>
            <option value="Bunnell">Bunnell</option>
            <option value="Bushnell">Bushnell</option>
            <option value="Callahan">Callahan</option>
            <option value="Callaway">Callaway</option>
            <option value="Campbellton">Campbellton</option>
            <option value="Cape Canaveral">Cape Canaveral</option>
            <option value="Cape Coral">Cape Coral</option>
            <option value="Carrabelle">Carrabelle</option>
            <option value="Caryville">Caryville</option>
            <option value="Casselberry">Casselberry</option>
            <option value="Cedar Key">Cedar Key</option>
            <option value="Center Hill">Center Hill</option>
            <option value="Century">Century</option>
            <option value="Chattahoochee">Chattahoochee</option>
            <option value="Chiefland">Chiefland</option>
            <option value="Chipley">Chipley</option>
            <option value="Cinco Bayou">Cinco Bayou</option>
            <option value="Clearwater">Clearwater</option>
            <option value="Clermont">Clermont</option>
            <option value="Clewiston">Clewiston</option>
            <option value="Cloud Lake">Cloud Lake</option>
            <option value="Cocoa">Cocoa</option>
            <option value="Cocoa Beach">Cocoa Beach</option>
            <option value="Coconut Creek">Coconut Creek</option>
            <option value="Coleman">Coleman</option>
            <option value="Cooper City">Cooper City</option>
            <option value="Coral Gables">Coral Gables</option>
            <option value="Coral Springs">Coral Springs</option>
            <option value="Cottondale">Cottondale</option>
            <option value="Crescent City">Crescent City</option>
            <option value="Crestview">Crestview</option>
            <option value="Cross City">Cross City</option>
            <option value="Crystal River">Crystal River</option>
            <option value="Cutler Bay">Cutler Bay</option>
            <option value="Dade City">Dade City</option>
            <option value="Dania Beach">Dania Beach</option>
            <option value="Davenport">Davenport</option>
            <option value="Davie">Davie</option>
            <option value="Daytona Beach">Daytona Beach</option>
            <option value="Daytona Beach Shores">Daytona Beach Shores</option>
            <option value="DeBary">DeBary</option>
            <option value="Deerfield Beach">Deerfield Beach</option>
            <option value="DeFuniak Springs">DeFuniak Springs</option>
            <option value="DeLand">DeLand</option>
            <option value="Delray Beach">Delray Beach</option>
            <option value="Deltona">Deltona</option>
            <option value="Destin">Destin</option>
            <option value="Doral">Doral</option>
            <option value="Dundee">Dundee</option>
            <option value="Dunedin">Dunedin</option>
            <option value="Dunnellon">Dunnellon</option>
            <option value="Eagle Lake">Eagle Lake</option>
            <option value="Eatonville">Eatonville</option>
            <option value="Ebro">Ebro</option>
            <option value="Edgewater">Edgewater</option>
            <option value="Edgewood">Edgewood</option>
            <option value="El Portal">El Portal</option>
            <option value="Estero">Estero</option>
            <option value="Esto">Esto</option>
            <option value="Eustis">Eustis</option>
            <option value="Everglades City">Everglades City</option>
            <option value="Fanning Springs">Fanning Springs</option>
            <option value="Fellsmere">Fellsmere</option>
            <option value="Fernandina Beach">Fernandina Beach</option>
            <option value="Flagler Beach">Flagler Beach</option>
            <option value="Florida City">Florida City</option>
            <option value="Fort Lauderdale">Fort Lauderdale</option>
            <option value="Fort Meade">Fort Meade</option>
            <option value="Fort Myers">Fort Myers</option>
            <option value="Fort Myers Beach">Fort Myers Beach</option>
            <option value="Fort Pierce">Fort Pierce</option>
            <option value="Fort Walton Beach">Fort Walton Beach</option>
            <option value="Fort White">Fort White</option>
            <option value="Freeport">Freeport</option>
            <option value="Frostproof">Frostproof</option>
            <option value="Fruitland Park">Fruitland Park</option>
            <option value="Gainesville">Gainesville</option>
            <option value="Glen Ridge">Glen Ridge</option>
            <option value="Glen St. Mary">Glen St. Mary</option>
            <option value="Golden Beach">Golden Beach</option>
            <option value="Golf">Golf</option>
            <option value="Graceville">Graceville</option>
            <option value="Grand Ridge">Grand Ridge</option>
            <option value="Grant-Valkaria">Grant-Valkaria</option>
            <option value="Green Cove Springs">Green Cove Springs</option>
            <option value="Greenacres">Greenacres</option>
            <option value="Greensboro">Greensboro</option>
            <option value="Greenville">Greenville</option>
            <option value="Greenwood">Greenwood</option>
            <option value="Gretna">Gretna</option>
            <option value="Groveland">Groveland</option>
            <option value="Gulf Breeze">Gulf Breeze</option>
            <option value="Gulf Stream">Gulf Stream</option>
            <option value="Gulfport">Gulfport</option>
            <option value="Haines City">Haines City</option>
            <option value="Hallandale Beach">Hallandale Beach</option>
            <option value="Hampton">Hampton</option>
            <option value="Havana">Havana</option>
            <option value="Haverhill">Haverhill</option>
            <option value="Hawthorne">Hawthorne</option>
            <option value="Hialeah">Hialeah</option>
            <option value="Hialeah Gardens">Hialeah Gardens</option>
            <option value="High Springs">High Springs</option>
            <option value="Highland Beach">Highland Beach</option>
            <option value="Highland Park">Highland Park</option>
            <option value="Hillcrest Heights">Hillcrest Heights</option>
            <option value="Hilliard">Hilliard</option>
            <option value="Hillsboro Beach">Hillsboro Beach</option>
            <option value="Holly Hill">Holly Hill</option>
            <option value="Hollywood">Hollywood</option>
            <option value="Holmes Beach">Holmes Beach</option>
            <option value="Homestead">Homestead</option>
            <option value="Horseshoe Beach">Horseshoe Beach</option>
            <option value="Howey-in-the-Hills">Howey-in-the-Hills</option>
            <option value="Hypoluxo">Hypoluxo</option>
            <option value="Indialantic">Indialantic</option>
            <option value="Indian Creek">Indian Creek</option>
            <option value="Indian Harbour Beach">Indian Harbour Beach</option>
            <option value="Indian River Shores">Indian River Shores</option>
            <option value="Indian Rocks Beach">Indian Rocks Beach</option>
            <option value="Indian Shores">Indian Shores</option>
            <option value="Indiantown">Indiantown</option>
            <option value="Inglis">Inglis</option>
            <option value="Interlachen">Interlachen</option>
            <option value="Inverness">Inverness</option>
            <option value="Islamorada">Islamorada</option>
            <option value="Jacksonville">Jacksonville</option>
            <option value="Jacksonville Beach">Jacksonville Beach</option>
            <option value="Jasper">Jasper</option>
            <option value="Jennings">Jennings</option>
            <option value="Juno Beach">Juno Beach</option>
            <option value="Jupiter">Jupiter</option>
            <option value="Jupiter Inlet Colony">Jupiter Inlet Colony</option>
            <option value="Jupiter Island">Jupiter Island</option>
            <option value="Kenneth City">Kenneth City</option>
            <option value="Key Biscayne">Key Biscayne</option>
            <option value="Key Colony Beach">Key Colony Beach</option>
            <option value="Key West">Key West</option>
            <option value="Kissimmee">Kissimmee</option>
            <option value="Lady Lake">Lady Lake</option>
            <option value="Lake Alfred">Lake Alfred</option>
            <option value="Lake City">Lake City</option>
            <option value="Lake Clarke Shores">Lake Clarke Shores</option>
            <option value="Lake Hamilton">Lake Hamilton</option>
            <option value="Lake Helen">Lake Helen</option>
            <option value="Lake Mary">Lake Mary</option>
            <option value="Lake Park">Lake Park</option>
            <option value="Lake Placid">Lake Placid</option>
            <option value="Lake Wales">Lake Wales</option>
            <option value="Lake Worth Beach">Lake Worth Beach</option>
            <option value="Lakeland">Lakeland</option>
            <option value="Lantana">Lantana</option>
            <option value="Largo">Largo</option>
            <option value="Lauderdale Lakes">Lauderdale Lakes</option>
            <option value="Lauderhill">Lauderhill</option>
            <option value="Lawtey">Lawtey</option>
            <option value="Leesburg">Leesburg</option>
            <option value="Lighthouse Point">Lighthouse Point</option>
            <option value="Live Oak">Live Oak</option>
            <option value="Longboat Key">Longboat Key</option>
            <option value="Longwood">Longwood</option>
            <option value="Lynn Haven">Lynn Haven</option>
            <option value="Macclenny">Macclenny</option>
            <option value="Madeira Beach">Madeira Beach</option>
            <option value="Madison">Madison</option>
            <option value="Maitland">Maitland</option>
            <option value="Manalapan">Manalapan</option>
            <option value="Mangonia Park">Mangonia Park</option>
            <option value="Marathon">Marathon</option>
            <option value="Marco Island">Marco Island</option>
            <option value="Margate">Margate</option>
            <option value="Marianna">Marianna</option>
            <option value="Mascotte">Mascotte</option>
            <option value="Mayo">Mayo</option>
            <option value="Melbourne">Melbourne</option>
            <option value="Melbourne Beach">Melbourne Beach</option>
            <option value="Miami">Miami</option>
            <option value="Miami Beach">Miami Beach</option>
            <option value="Miami Gardens">Miami Gardens</option>
            <option value="Miami Lakes">Miami Lakes</option>
            <option value="Miami Shores">Miami Shores</option>
            <option value="Miami Springs">Miami Springs</option>
            <option value="Micanopy">Micanopy</option>
            <option value="Milton">Milton</option>
            <option value="Miramar">Miramar</option>
            <option value="Monticello">Monticello</option>
            <option value="Mount Dora">Mount Dora</option>
            <option value="Mulberry">Mulberry</option>
            <option value="Naples">Naples</option>
            <option value="Neptune Beach">Neptune Beach</option>
            <option value="New Port Richey">New Port Richey</option>
            <option value="New Smyrna Beach">New Smyrna Beach</option>
            <option value="Newberry">Newberry</option>
            <option value="Niceville">Niceville</option>
            <option value="North Bay Village">North Bay Village</option>
            <option value="North Lauderdale">North Lauderdale</option>
            <option value="North Miami">North Miami</option>
            <option value="North Miami Beach">North Miami Beach</option>
            <option value="North Palm Beach">North Palm Beach</option>
            <option value="North Port">North Port</option>
            <option value="North Redington Beach">North Redington Beach</option>
            <option value="Oakland">Oakland</option>
            <option value="Oakland Park">Oakland Park</option>
            <option value="Ocala">Ocala</option>
            <option value="Ocean Ridge">Ocean Ridge</option>
            <option value="Ocoee">Ocoee</option>
            <option value="Oldsmar">Oldsmar</option>
            <option value="Opa-locka">Opa-locka</option>
            <option value="Orange City">Orange City</option>
            <option value="Orange Park">Orange Park</option>
            <option value="Orlando">Orlando</option>
            <option value="Ormond Beach">Ormond Beach</option>
            <option value="Oviedo">Oviedo</option>
            <option value="Pahokee">Pahokee</option>
            <option value="Palatka">Palatka</option>
            <option value="Palm Bay">Palm Bay</option>
            <option value="Palm Beach">Palm Beach</option>
            <option value="Palm Beach Gardens">Palm Beach Gardens</option>
            <option value="Palm Beach Shores">Palm Beach Shores</option>
            <option value="Palm Coast">Palm Coast</option>
            <option value="Palm Springs">Palm Springs</option>
            <option value="Palmetto">Palmetto</option>
            <option value="Panama City">Panama City</option>
            <option value="Panama City Beach">Panama City Beach</option>
            <option value="Parker">Parker</option>
            <option value="Parkland">Parkland</option>
            <option value="Pembroke Park">Pembroke Park</option>
            <option value="Pembroke Pines">Pembroke Pines</option>
            <option value="Perry">Perry</option>
            <option value="Pinecrest">Pinecrest</option>
            <option value="Pinellas Park">Pinellas Park</option>
            <option value="Plant City">Plant City</option>
            <option value="Polk City">Polk City</option>
            <option value="Plantation">Plantation</option>
            <option value="Pompano Beach">Pompano Beach</option>
            <option value="Ponce Inlet">Ponce Inlet</option>
            <option value="Port Orange">Port Orange</option>
            <option value="Port Richey">Port Richey</option>
            <option value="Port St. Joe">Port St. Joe</option>
            <option value="Port St. Lucie">Port St. Lucie</option>
            <option value="Punta Gorda">Punta Gorda</option>
            <option value="Quincy">Quincy</option>
            <option value="Redington Beach">Redington Beach</option>
            <option value="Redington Shores">Redington Shores</option>
            <option value="Riviera Beach">Riviera Beach</option>
            <option value="Rockledge">Rockledge</option>
            <option value="Royal Palm Beach">Royal Palm Beach</option>
            <option value="Safety Harbor">Safety Harbor</option>
            <option value="Sanford">Sanford</option>
            <option value="Sanibel">Sanibel</option>
            <option value="Sarasota">Sarasota</option>
            <option value="Satellite Beach">Satellite Beach</option>
            <option value="Sea Ranch Lakes">Sea Ranch Lakes</option>
            <option value="Sebastian">Sebastian</option>
            <option value="Sebring">Sebring</option>
            <option value="Seminole">Seminole</option>
            <option value="South Bay">South Bay</option>
            <option value="South Daytona">South Daytona</option>
            <option value="South Miami">South Miami</option>
            <option value="South Palm Beach">South Palm Beach</option>
            <option value="South Pasadena">South Pasadena</option>
            <option value="Springfield">Springfield</option>
            <option value="St. Augustine">St. Augustine</option>
            <option value="St. Augustine Beach">St. Augustine Beach</option>
            <option value="St. Cloud">St. Cloud</option>
            <option value="St. Pete Beach">St. Pete Beach</option>
            <option value="St. Petersburg">St. Petersburg</option>
            <option value="Starke">Starke</option>
            <option value="Stuart">Stuart</option>
            <option value="Sunny Isles Beach">Sunny Isles Beach</option>
            <option value="Sunrise">Sunrise</option>
            <option value="Surfside">Surfside</option>
            <option value="Sweetwater">Sweetwater</option>
            <option value="Tallahassee">Tallahassee</option>
            <option value="Tamarac">Tamarac</option>
            <option value="Tampa">Tampa</option>
            <option value="Tarpon Springs">Tarpon Springs</option>
            <option value="Tavares">Tavares</option>
            <option value="Temple Terrace">Temple Terrace</option>
            <option value="Tequesta">Tequesta</option>
            <option value="Titusville">Titusville</option>
            <option value="Treasure Island">Treasure Island</option>
            <option value="Trenton">Trenton</option>
            <option value="Umatilla">Umatilla</option>
            <option value="Valparaiso">Valparaiso</option>
            <option value="Venice">Venice</option>
            <option value="Vernon">Vernon</option>
            <option value="Vero Beach">Vero Beach</option>
            <option value="Virginia Gardens">Virginia Gardens</option>
            <option value="Waldo">Waldo</option>
            <option value="Wauchula">Wauchula</option>
            <option value="Wausau">Wausau</option>
            <option value="Webster">Webster</option>
            <option value="Welaka">Welaka</option>
            <option value="Wellington">Wellington</option>
            <option value="Westlake">Westlake</option>
            <option value="West Melbourne">West Melbourne</option>
            <option value="West Miami">West Miami</option>
            <option value="West Palm Beach">West Palm Beach</option>
            <option value="West Park">West Park</option>
            <option value="Weston">Weston</option>
            <option value="Westville">Westville</option>
            <option value="Wewahitchka">Wewahitchka</option>
            <option value="White Springs">White Springs</option>
            <option value="Wildwood">Wildwood</option>
            <option value="Williston">Williston</option>
            <option value="Wilton Manors">Wilton Manors</option>
            <option value="Windermere">Windermere</option>
            <option value="Winter Garden">Winter Garden</option>
            <option value="Winter Haven">Winter Haven</option>
            <option value="Winter Park">Winter Park</option>
            <option value="Winter Springs">Winter Springs</option>
            <option value="Worthington Springs">Worthington Springs</option>
            <option value="Yankeetown">Yankeetown</option>
            <option value="Zephyrhills">Zephyrhills</option>
            <option value="Zolfo Springs">Zolfo Springs</option>
          </select>
        </div>
      </div>
      <div className="formGroup">
        <label>Address of Alien Contact <span style={{color: '#dc3545'}}>*</span></label>
        <input
          type="text"
          name="contactStreetAddress"
          value={formData.contactStreetAddress}
          onChange={handleTitleCaseInputChange}
          className="modern-input"
          placeholder="House number and street name"
          required
        />
      </div>



      {/* 6. Illegal Alien's Personal Information */}
      <h3>6. Suspected Alien's Personal Information</h3>
      <div className="formRow">
        <div className="formGroup">
          <label>Alien's First Name <span style={{color: '#dc3545'}}>*</span></label>
          <input
            type="text"
            name="firstName"
            value={formData.firstName}
            onChange={handleTitleCaseInputChange}
            className="modern-input"
            required
          />
        </div>
        <div className="formGroup">
          <label>Alien's Middle Name <span style={{color: '#dc3545'}}>*</span></label>
          <input
            type="text"
            name="middleName"
            value={formData.middleName}
            onChange={handleTitleCaseInputChange}
            className="modern-input"
            placeholder="Use NA if not available"
            title="Use NA if not available"
            required
          />
          <small style={{ color: '#666', fontSize: '12px', fontStyle: 'italic', display: 'block', marginTop: '4px' }}>
            Use NA if not available
          </small>
        </div>
        <div className="formGroup">
          <label>Alien's Last Name <span style={{color: '#dc3545'}}>*</span></label>
          <input
            type="text"
            name="lastName"
            value={formData.lastName}
            onChange={handleTitleCaseInputChange}
            className="modern-input"
            required
          />
        </div>
      </div>
      <div className="formRow">
        <div className="formGroup">
          <label>Alien's Country of Citizenship <span style={{color: '#dc3545'}}>*</span></label>
          <select
            name="countryOfCitizenship"
            value={formData.countryOfCitizenship}
            onChange={handleInputChange}
            className="modern-input"
            required
          >
            <option value="">Select Country</option>
            {COUNTRIES.map(country => (
              <option key={country.value} value={country.label}>
                {country.label}
              </option>
            ))}
          </select>
        </div>
        <div className="formGroup">
          <label>Alien's Registration Number (A-number)</label>
          <input
            type="text"
            name="alienRegistrationNumber"
            value={formData.alienRegistrationNumber}
            onChange={handleInputChange}
            className="modern-input"
            placeholder="Numbers only, no letters"
            pattern="[0-9]*"
            inputMode="numeric"
            onInput={(e) => {
              e.target.value = e.target.value.replace(/[^0-9]/g, '');
              handleInputChange(e);
            }}
          />
        </div>
      </div>
      <div className="formRow">
        <div className="formGroup checkboxGroup">
          <label>
            <input
              type="checkbox"
              name="hasDualCitizenship"
              checked={formData.hasDualCitizenship}
              onChange={handleInputChange}
            />
            Does Alien Have Dual Citizenship?
          </label>
        </div>
        {formData.hasDualCitizenship && (
          <div className="formGroup">
            <label>Dual Citizenship Country</label>
            <select
              name="dualCitizenshipCountry"
              value={formData.dualCitizenshipCountry}
              onChange={handleInputChange}
              className="modern-input"
              required
            >
              <option value="">Select Second Country</option>
              {COUNTRIES.map(country => (
                <option key={country.value} value={country.label}>
                  {country.label}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>
      <div className="formRow">
        <div className="formGroup">
          <label>Alien's Date of Birth <span style={{color: '#dc3545'}}>*</span></label>
          <input
            type="date"
            name="dateOfBirth"
            value={formData.dateOfBirth}
            onChange={handleDateInputChange}
            onFocus={handleDateFocus}
            onClick={handleDateClick}
            className="modern-input"
            min="1000-01-01"
            max="9999-12-31"
            required
          />
        </div>
        <div className="formGroup">
          <label>Alien's Sex <span style={{color: '#dc3545'}}>*</span></label>
          <select
            name="sex"
            value={formData.sex}
            onChange={handleInputChange}
            className="modern-input"
            required
          >
            <option value="">Select Sex</option>
            <option value="Male">Male</option>
            <option value="Female">Female</option>
          </select>
        </div>
      </div>

      {/* 7. Illegal Alien's Current Address */}
      <h3>7. Suspected Alien's Current Address</h3>
      <div className="formGroup">
        <label>Alien's Current Street Address</label>
        <input
          type="text"
          name="currentStreetAddress"
          value={formData.currentStreetAddress}
          onChange={handleTitleCaseInputChange}
          className="modern-input"
          placeholder='Street address only - Ex: 1234 Test Street - Do not use abbreviations'
        />
      </div>
      <div className="formRow">
        <div className="formGroup">
          <label>Alien's Current City</label>
          <input
            type="text"
            name="currentCity"
            value={formData.currentCity}
            onChange={handleTitleCaseInputChange}
            className="modern-input"
          />
        </div>
        <div className="formGroup">
          <label>Alien's Current State</label>
          <select
            name="currentState"
            value={formData.currentState}
            onChange={handleInputChange}
            className="modern-input"
          >
            <option value="">Select State</option>
            {US_STATES.map(state => (
              <option key={state.value} value={state.value}>
                {state.label}
              </option>
            ))}
          </select>
        </div>
        <div className="formGroup">
          <label>Alien's Current Zip Code</label>
          <input
            type="text"
            name="currentZipCode"
            value={formData.currentZipCode}
            onChange={handleInputChange}
            className="modern-input"
            pattern="[0-9]{5}"
            inputMode="numeric"
            maxLength="5"
            placeholder="5 digits only"
            onInput={(e) => {
              e.target.value = e.target.value.replace(/[^0-9]/g, '').slice(0, 5);
              handleInputChange(e);
            }}
          />
        </div>
      </div>
      <div className="formGroup">
        <label>Alien's Phone Number</label>
        <input
          type="tel"
          name="phoneNumber"
          value={formData.phoneNumber}
          onChange={(e) => {
            // Format phone number as XXX-XXX-XXXX
            let value = e.target.value.replace(/\D/g, ''); // Remove non-digits
            // Limit to 10 digits
            value = value.slice(0, 10);
            // Format as XXX-XXX-XXXX
            if (value.length >= 6) {
              value = value.slice(0, 3) + '-' + value.slice(3, 6) + '-' + value.slice(6);
            } else if (value.length >= 3) {
              value = value.slice(0, 3) + '-' + value.slice(3);
            }

            const syntheticEvent = {
              target: {
                name: 'phoneNumber',
                value: value,
                type: 'tel'
              }
            };
            handleInputChange(syntheticEvent);
          }}
          className="modern-input"
          placeholder="555-555-5555"
          title="Enter valid 10-digit phone number XXX-XXX-XXXX"
        />
        <small style={{ color: '#666', fontSize: '12px', fontStyle: 'italic', display: 'block', marginTop: '4px' }}>
          Format: XXX-XXX-XXXX (10 digits)
        </small>
      </div>

      {/* 8. Vehicle Information */}
      <h3>8. Vehicle Information</h3>
      <div className="formRow">
        <div className="formGroup">
          <label>Vehicle Year</label>
          <input
            type="text"
            name="vehicleYear"
            value={formData.vehicleYear}
            onChange={handleInputChange}
            className="modern-input"
            placeholder="YYYY (e.g., 2020)"
            pattern="[0-9]{4}"
            maxLength="4"
            title="Please enter a 4-digit year"
          />
        </div>
        <div className="formGroup">
          <label>Vehicle Make</label>
          <input
            type="text"
            name="vehicleMake"
            value={formData.vehicleMake}
            onChange={handleTitleCaseInputChange}
            className="modern-input"
            placeholder="Toyota, Ford, etc."
          />
        </div>
        <div className="formGroup">
          <label>Vehicle Model</label>
          <input
            type="text"
            name="vehicleModel"
            value={formData.vehicleModel}
            onChange={handleTitleCaseInputChange}
            className="modern-input"
            placeholder="Camry, F-150, etc."
          />
        </div>
      </div>

      <div className="formRow">
        <div className="formGroup">
          <label>Vehicle Tag</label>
          <input
            type="text"
            name="vehicleTag"
            value={formData.vehicleTag}
            onChange={handleTitleCaseInputChange}
            className="modern-input"
            placeholder="License plate number"
          />
        </div>
        <div className="formGroup">
          <label>Vehicle Tag State</label>
          <select
            name="vehicleTagState"
            value={formData.vehicleTagState}
            onChange={handleInputChange}
            className="modern-input"
          >
            <option value="">Select State</option>
            {US_STATES.map(state => (
              <option key={state.value} value={state.value}>
                {state.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* 9. Suspected Alien's Current Employer Info */}
      <h3>9. Suspected Alien's Current Employer Info</h3>
      <div className="formGroup">
        <label>Alien's Current Employer Name</label>
        <input
          type="text"
          name="employerName"
          value={formData.employerName}
          onChange={handleTitleCaseInputChange}
          className="modern-input"
        />
      </div>
      <div className="formGroup">
        <label>Alien's Current Employer's Street Address</label>
        <input
          type="text"
          name="employerStreetAddress"
          value={formData.employerStreetAddress}
          onChange={handleTitleCaseInputChange}
          className="modern-input"
          placeholder="Include Street Address, City and State - Ex: 1234 Test Street, Miami, Florida - Do not use abbreviations"
        />
      </div>




      {/* 10. Additional Information */}
      <h3>10. Additional Information</h3>
      <div className="formGroup">
        <label>Does Alien Have Gang Affiliations? *</label>
        <select
          name="hasGangAffiliations"
          value={formData.hasGangAffiliations === true ? 'Yes' : formData.hasGangAffiliations === false ? 'No' : ''}
          onChange={(e) => {
            const value = e.target.value;
            handleInputChange({
              target: {
                name: 'hasGangAffiliations',
                type: 'select',
                value: value === 'Yes' ? true : value === 'No' ? false : undefined
              }
            });
          }}
          className="modern-input"
          required
        >
          <option value="">-- Select an option --</option>
          <option value="Yes">Yes</option>
          <option value="No">No</option>
        </select>
      </div>
      {formData.hasGangAffiliations && (
        <div className="formGroup">
          <label>Gang Name</label>
          <input
            type="text"
            name="gangName"
            value={formData.gangName}
            onChange={handleTitleCaseInputChange}
            className="modern-input"
          />
        </div>
      )}


      <div className="formGroup">
        <label>Provide Description of Contact or Administrative Actions Taken<br/><span style={{fontSize: '0.85em', color: '#666'}}>Max 120 characters: ({formData.contactDescription?.length || 0}/120)</span></label>
        <textarea
          name="contactDescription"
          value={formData.contactDescription}
          onChange={handleInputChange}
          maxLength="120"
          className="modern-input"
          rows="4"
          placeholder="Describe the contact or administrative actions taken during this encounter..."
        />
      </div>


      <div className="formGroup">
        <label>Was Contact Made with Any Federal Agency Regarding This Alien Contact?</label>
        <select
          name="federalAgencyContact"
          value={formData.federalAgencyContact || ''}
          onChange={handleInputChange}
          className="modern-input"
        >
          <option value="">-- Select an option --</option>
          <option value="Immigration and Customs Enforcement Homeland Security Investigations">Immigration and Customs Enforcement Homeland Security Investigations</option>
          <option value="Immigration and Customs Enforcement (ICE) Enforcement and Removal Operations">Immigration and Customs Enforcement (ICE) Enforcement and Removal Operations</option>
          <option value="Customs and Border Protection">Customs and Border Protection</option>
          <option value="Customs and Border Protection United States Border Patrol">Customs and Border Protection United States Border Patrol</option>
          <option value="Customs and Border Protection Air and Marine Operations">Customs and Border Protection Air and Marine Operations</option>
          <option value="Customs and Border Protection Office of Field Operations">Customs and Border Protection Office of Field Operations</option>
          <option value="United States Coast Guard">United States Coast Guard</option>
          <option value="No federal agency contacted">No federal agency contacted</option>
        </select>
      </div>


      <div className="formGroup">
        <label>Alien Contact was in Collaboration with which Agency (choose lead agency)?</label>
        <select
          name="collaborationAgency"
          value={formData.collaborationAgency || ''}
          onChange={handleInputChange}
          className="modern-input"
        >
          <option value="">-- Select an option --</option>
          <option value="Not in Collaboration with an Agency">Not in Collaboration with an Agency</option>
          <option value="Other Local Agency Only">Other Local Agency Only</option>
          <option value="Other State Agency Only">Other State Agency Only</option>
          <option value="Immigration and Customs Enforcement Homeland Security Investigations">Immigration and Customs Enforcement Homeland Security Investigations</option>
          <option value="Immigration and Customs Enforcement (ICE) Enforcement and Removal Operations">Immigration and Customs Enforcement (ICE) Enforcement and Removal Operations</option>
          <option value="Customs and Border Protection">Customs and Border Protection</option>
          <option value="Customs and Border Protection United States Border Patrol">Customs and Border Protection United States Border Patrol</option>
          <option value="Customs and Border Protection Air and Marine Operations">Customs and Border Protection Air and Marine Operations</option>
          <option value="Customs and Border Protection Office of Field Operations">Customs and Border Protection Office of Field Operations</option>
          <option value="United States Coast Guard">United States Coast Guard</option>
        </select>
      </div>


      <div className="formGroup">
        <label>Does Alien Have a Criminal History? *</label>
        <select
          name="hasCriminalHistory"
          value={(() => {
            const value = formData.hasCriminalHistory;
            // Handle both string values and legacy boolean values
            if (typeof value === 'string') {
              return ['Yes', 'No', 'Unknown'].includes(value) ? value : '';
            } else if (typeof value === 'boolean') {
              return value ? 'Yes' : 'No';
            } else {
              return '';
            }
          })()}
          onChange={(e) => {
            const value = e.target.value;
            handleInputChange({
              target: {
                name: 'hasCriminalHistory',
                type: 'select',
                value: value || undefined // Store the string value directly
              }
            });
          }}
          className="modern-input"
          required
        >
          <option value="">-- Select an option --</option>
          <option value="Yes">Yes</option>
          <option value="No">No</option>
          <option value="Unknown">Unknown</option>
        </select>
      </div>

      {/* 11. Citizenship Status Verification */}
      <h3>11. Citizenship Status Verification</h3>
      <div className="formGroup">
        <label>Was Citizenship Status Verified with ICE Call Center? *</label>
        <select
          name="citizenshipVerifiedWithIce"
          value={formData.citizenshipVerifiedWithIce || ''}
          onChange={handleInputChange}
          className="modern-input"
          required
        >
          <option value="">-- Select an option --</option>
          <option value="Yes">Yes</option>
          <option value="No">No</option>
        </select>
      </div>
      <div className="formGroup">
        <label>Name and Identification of Call Center Contact <span style={{color: '#dc3545'}}>*</span></label>
        <input
          type="text"
          name="iceCallCenterContact"
          value={formData.iceCallCenterContact}
          onChange={handleTitleCaseInputChange}
          className="modern-input"
          placeholder="NA if Citizenship was Not Verified"
          required
        />
      </div>

      {/* 12. Arrest Information */}
      <h3>12. Arrest Information</h3>

      {/* 12.1. Was Alien Arrested During This Contact */}
      <div className="formGroup">
        <label>Was Alien Arrested During This Contact? *</label>
        <select
          name="wasArrested"
          value={formData.wasArrested || ''}
          onChange={handleInputChange}
          className="modern-input"
          required
        >
          <option value="">-- Select an option --</option>
          <option value="Yes - Local/State Charges">Yes - Local/State Charges</option>
          <option value="Yes - Federal Immigration Charges">Yes - Federal Immigration Charges</option>
          <option value="Not Arrested">Not Arrested</option>
        </select>
      </div>

      {/* 12.2. 287(g) Authority Usage */}
      <div className="formGroup">
        <label>If Arrested, 287(g) Authority Was Used for this Arrest <span style={{color: '#dc3545'}}>*</span></label>
        <select
          name="used287gAuthority"
          value={formData.used287gAuthority || ''}
          onChange={(e) => {
            const value = e.target.value;
            handleInputChange({
              target: {
                name: 'used287gAuthority',
                type: 'select',
                value: value || null
              }
            });
          }}
          className="modern-input"
          required
        >
          <option value="">-- Select an option --</option>
          <option value="Yes">Yes</option>
          <option value="No">No</option>
          <option value="Alien was not arrested">Alien was not arrested</option>
        </select>
      </div>
      {(formData.wasArrested === 'Yes - Local/State Charges' || formData.wasArrested === 'Yes - Federal Immigration Charges') && (
        <div className="formGroup">
          <label>Arrest Charges (in order of severity)</label>
          <textarea
            name="arrestCharges"
            value={formData.arrestCharges}
            onChange={handleInputChange}
            className="modern-input"
            rows="3"
          ></textarea>
        </div>
      )}
      {/* 11.4. ICE Contact Status - FDLE Field el-42 */}
      <div className="formGroup">
        <label>Was ICE Contacted? *</label>
        <select
          name="iceContacted"
          value={formData.iceContacted || ''}
          onChange={handleInputChange}
          className="modern-input"
          required
        >
          <option value="">-- Select an option --</option>
          <option value="Yes">Yes</option>
          <option value="No">No</option>
        </select>
      </div>
      {/* 11.5. ICE Response Status - FDLE Field el-43 - Always visible */}
      <div className="formGroup">
        <label>If ICE Contacted: Did ICE Respond? *</label>
        <select
          name="iceResponded"
          value={formData.iceResponded || ''}
          onChange={handleInputChange}
          className="modern-input"
          required
        >
          <option value="">-- Select an option --</option>
          <option value="Yes">Yes</option>
          <option value="No">No</option>
          <option value="ICE was not contacted">ICE was not contacted</option>
        </select>
      </div>
      {/* 11.6. ICE Release Status - FDLE Field el-45 - Always visible */}
      <div>
        <div className="formGroup">
          <label>If ICE Responded: Was Alien Released by ICE? *</label>
          <select
            name="alienReleasedByIce"
            value={formData.alienReleasedByIce || ''}
            onChange={handleInputChange}
            className="modern-input"
            required
          >
            <option value="">-- Select an option --</option>
            <option value="Yes">Yes</option>
            <option value="No">No</option>
            <option value="ICE did not respond">ICE did not respond</option>
          </select>
        </div>
        {/* Always show "Why not detained?" field as well */}
        <div className="formGroup">
          <label>Why not detained?<br/><span style={{fontSize: '0.85em', color: '#666'}}>Max 120 characters: ({formData.notDetainedReason?.length || 0}/120)</span></label>
          <input
            type="text"
            name="notDetainedReason"
            value={formData.notDetainedReason}
            onChange={handleTitleCaseInputChange}
            maxLength="120"
            className="modern-input"
          />
        </div>
      </div>
      {/* 11.7. ICE No Response Reason - Always visible */}
      <div className="formGroup">
        <label>Why did ICE not respond?<br/><span style={{fontSize: '0.85em', color: '#666'}}>Max 120 characters: ({formData.iceNoResponseReason?.length || 0}/120)</span></label>
        <input
          type="text"
          name="iceNoResponseReason"
          value={formData.iceNoResponseReason}
          onChange={handleTitleCaseInputChange}
          maxLength="120"
          className="modern-input"
        />
      </div>

      {/* 13. Photo */}
      <h3>13. Photo</h3>
      <div className="formGroup">
        <label>Upload Photo</label>
        <input
          type="file"
          name="photoFile"
          ref={photoFileInputRef}
          onChange={handleInputChange}
          className="modern-input"
          accept="image/*"
        />
        <div style={{ marginTop: '8px', fontSize: '14px', color: '#666', fontStyle: 'italic' }}>
          Note: Make sure the photo is clear, and subject is identifiable from the neck up.
        </div>
        {formData.photoFile && (
          <div style={{ marginTop: '10px', fontSize: '14px', color: '#666' }}>
            Selected: {formData.photoFile.name}
          </div>
        )}
        {formData.photoUrl && !formData.photoFile && (
          <div style={{ marginTop: '10px' }}>
            <img
              src={formData.photoUrl.startsWith('http') ? formData.photoUrl : `${API_BASE_URL}${formData.photoUrl}`}
              alt="Current"
              style={{
                width: '200px',
                height: '200px',
                objectFit: 'cover',
                border: '1px solid #ddd',
                borderRadius: '4px'
              }}
            />
          </div>
        )}
      </div>

      {/* DIO Investigation Details Header */}
      <h2 style={{
        color: 'var(--primary-color)',
        fontSize: '20px',
        marginTop: '30px',
        marginBottom: '20px',
        textAlign: 'left',
        borderBottom: '2px solid var(--primary-color)',
        paddingBottom: '10px'
      }}>
        DIO Investigation Details
      </h2>

      {/* Is Submitter DIO Field */}
      <div className="formGroup">
        <label>Is submitter DIO? <span style={{color: '#dc3545'}}>*</span></label>
        <select
          name="is_submitter_dio"
          value={formData.is_submitter_dio === true ? "true" : formData.is_submitter_dio === false ? "false" : ""}
          onChange={(e) => {
            const value = e.target.value;
            const booleanValue = value === "true" ? true : value === "false" ? false : "";
            const syntheticEvent = {
              target: {
                name: 'is_submitter_dio',
                value: booleanValue,
                type: 'select'
              }
            };
            handleInputChange(syntheticEvent);
          }}
          className="modern-input"
          required
        >
          <option value="">Select...</option>
          <option value="true">Yes</option>
          <option value="false">No</option>
        </select>
      </div>

      {/* Non-Criminal Arrest Field - Only show when DIO is Yes */}
      {formData.is_submitter_dio === true && (
        <div className="formGroup">
          <label>Non-Criminal Arrest? <span style={{color: '#dc3545'}}>*</span></label>
          <select
            name="is_non_criminal_arrest"
            value={formData.is_non_criminal_arrest === true ? "true" : formData.is_non_criminal_arrest === false ? "false" : ""}
            onChange={(e) => {
              const value = e.target.value;
              const booleanValue = value === "true" ? true : value === "false" ? false : "";
              const syntheticEvent = {
                target: {
                  name: 'is_non_criminal_arrest',
                  value: booleanValue,
                  type: 'select'
                }
              };
              handleInputChange(syntheticEvent);
            }}
            className="modern-input"
            required
          >
            <option value="">Select...</option>
            <option value="true">Yes</option>
            <option value="false">No</option>
          </select>
        </div>
      )}

      {/* TFM Arrest Sheet Fields - Only show when DIO is Yes AND Non-Criminal Arrest is Yes */}
      {formData.is_submitter_dio === true && formData.is_non_criminal_arrest === true && (
        <>
          <h2 style={{
            color: 'var(--primary-color)',
            fontSize: '20px',
            marginTop: '30px',
            marginBottom: '20px',
            textAlign: 'left',
            borderBottom: '2px solid var(--primary-color)',
            paddingBottom: '10px'
          }}>
            TFM Arrest Sheet
          </h2>

          {/* 1. Law Enforcement Agency */}
          <div className="formGroup">
            <label>Law Enforcement Agency:</label>
            <input
              type="text"
              name="tfmLawEnforcementAgency"
              value={formData.tfmLawEnforcementAgency}
              onChange={handleTitleCaseInputChange}
              maxLength="120"
              className="modern-input"
              placeholder="Enter law enforcement agency"
            />
          </div>

          {/* 2. 287(g) Task Force Officer Name */}
          <div className="formGroup">
            <label>287(g) Task Force Officer Name:</label>
            <input
              type="text"
              name="tfmTaskForceOfficerName"
              value={formData.tfmTaskForceOfficerName}
              onChange={handleTitleCaseInputChange}
              maxLength="120"
              className="modern-input"
              placeholder="Enter task force officer name"
            />
          </div>

          {/* 3. Badge Number */}
          <div className="formGroup">
            <label>Badge Number:</label>
            <input
              type="text"
              name="tfmBadgeNumber"
              value={formData.tfmBadgeNumber}
              onChange={handleInputChange}
              maxLength="120"
              className="modern-input"
              placeholder="Enter badge number"
            />
          </div>

          {/* 4. ICE Credential (serial) number */}
          <div className="formGroup">
            <label>ICE Credential (serial) number:</label>
            <input
              type="text"
              name="tfmIceCredentialNumber"
              value={formData.tfmIceCredentialNumber}
              onChange={handleInputChange}
              maxLength="120"
              className="modern-input"
              placeholder="Enter ICE credential number"
            />
          </div>

          {/* 5. Cell Phone Number */}
          <div className="formGroup">
            <label>Cell Phone Number:</label>
            <input
              type="tel"
              name="tfmCellPhone"
              value={formData.tfmCellPhone}
              onChange={(e) => {
                // Format phone number as XXX-XXX-XXXX
                let value = e.target.value.replace(/\D/g, '');
                value = value.slice(0, 10);
                if (value.length >= 6) {
                  value = value.slice(0, 3) + '-' + value.slice(3, 6) + '-' + value.slice(6, 10);
                } else if (value.length >= 3) {
                  value = value.slice(0, 3) + '-' + value.slice(3);
                }
                const syntheticEvent = {
                  target: { name: 'tfmCellPhone', value: value, type: 'tel' }
                };
                handleInputChange(syntheticEvent);
              }}
              className="modern-input"
              placeholder="555-555-5555"
            />
          </div>

          {/* 6. Date of Arrest & Arrest Time */}
          <div className="formRow">
            <div className="formGroup">
              <label>Date of Arrest:</label>
              <input
                type="date"
                name="tfmArrestDate"
                value={formData.tfmArrestDate}
                onChange={handleDateInputChange}
                onFocus={handleDateFocus}
                onClick={handleDateClick}
                className="modern-input"
                min="1000-01-01"
                max="9999-12-31"
              />
            </div>
            <div className="formGroup">
              <label>Arrest Time:</label>
              <input
                type="time"
                name="tfmArrestTime"
                value={formData.tfmArrestTime}
                onChange={handleInputChange}
                className="modern-input"
              />
            </div>
          </div>

          {/* 7. Case Number */}
          <div className="formGroup">
            <label>Case Number:</label>
            <input
              type="text"
              name="tfmEventNumber"
              value={formData.tfmEventNumber}
              onChange={handleInputChange}
              maxLength="120"
              className="modern-input"
              placeholder="Enter case number"
            />
          </div>

          {/* 8. Approval to Arrest Granted By ICE Officer Name & Date/Time */}
          <div className="formRow">
            <div className="formGroup">
              <label>Approval to Arrest Granted By ICE Officer Name:</label>
              <input
                type="text"
                name="tfmIceApprovalOfficerName"
                value={formData.tfmIceApprovalOfficerName}
                onChange={handleTitleCaseInputChange}
                maxLength="120"
                className="modern-input"
                placeholder="Enter ICE officer name"
              />
            </div>
            <div className="formGroup">
              <label>Date/Time:</label>
              <input
                type="datetime-local"
                name="tfmIceApprovalDatetime"
                value={formData.tfmIceApprovalDatetime}
                onChange={handleInputChange}
                className="modern-input"
              />
            </div>
          </div>

          {/* 9. Arrest Location */}
          <div className="formGroup">
            <label>Arrest Location:</label>
            <input
              type="text"
              name="tfmArrestLocation"
              value={formData.tfmArrestLocation}
              onChange={handleTitleCaseInputChange}
              maxLength="120"
              className="modern-input"
              placeholder="Enter arrest location"
            />
          </div>

          {/* 10. Alien Information Section */}
          <h3 style={{ marginTop: '20px', marginBottom: '15px', color: '#333' }}>Alien Information:</h3>

          <div className="formRow">
            <div className="formGroup">
              <label>Name:</label>
              <input
                type="text"
                name="tfmAlienName"
                value={formData.tfmAlienName}
                onChange={handleTitleCaseInputChange}
                maxLength="120"
                className="modern-input"
                placeholder="Enter alien full name"
              />
            </div>
            <div className="formGroup">
              <label>DOB:</label>
              <input
                type="date"
                name="tfmAlienDob"
                value={formData.tfmAlienDob}
                onChange={handleDateInputChange}
                onFocus={handleDateFocus}
                onClick={handleDateClick}
                className="modern-input"
                min="1000-01-01"
                max="9999-12-31"
              />
            </div>
          </div>

          <div className="formRow">
            <div className="formGroup">
              <label>A Number:</label>
              <input
                type="text"
                name="tfmAlienANumber"
                value={formData.tfmAlienANumber}
                onChange={handleInputChange}
                maxLength="120"
                className="modern-input"
                placeholder="Enter A Number"
              />
            </div>
            <div className="formGroup">
              <label>Sex:</label>
              <select
                name="tfmAlienSex"
                value={formData.tfmAlienSex}
                onChange={handleInputChange}
                className="modern-input"
              >
                <option value="">-- Select an option --</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
              </select>
            </div>
          </div>

          <div className="formRow">
            <div className="formGroup">
              <label>Nationality:</label>
              <input
                type="text"
                name="tfmAlienNationality"
                value={formData.tfmAlienNationality}
                onChange={handleTitleCaseInputChange}
                maxLength="120"
                className="modern-input"
                placeholder="Enter nationality"
              />
            </div>
            <div className="formGroup">
              <label>FBI Number:</label>
              <input
                type="text"
                name="tfmAlienFbiNumber"
                value={formData.tfmAlienFbiNumber}
                onChange={handleInputChange}
                maxLength="120"
                className="modern-input"
                placeholder="Enter FBI number"
              />
            </div>
          </div>

          {/* 11. Consent to search/Who granted consent */}
          <div className="formGroup">
            <label>Consent to search/Who granted consent:</label>
            <input
              type="text"
              name="tfmConsentGrantedBy"
              value={formData.tfmConsentGrantedBy}
              onChange={handleTitleCaseInputChange}
              maxLength="120"
              className="modern-input"
              placeholder="Enter name of person who granted consent"
            />
          </div>

          {/* 12. Time consent was granted & Time search completed */}
          <div className="formRow">
            <div className="formGroup">
              <label>Time consent was granted:</label>
              <input
                type="time"
                name="tfmConsentTime"
                value={formData.tfmConsentTime}
                onChange={handleInputChange}
                className="modern-input"
              />
            </div>
            <div className="formGroup">
              <label>Time search completed:</label>
              <input
                type="time"
                name="tfmSearchCompletedTime"
                value={formData.tfmSearchCompletedTime}
                onChange={handleInputChange}
                className="modern-input"
              />
            </div>
          </div>

          <div className="formGroup">
            <label>Hours Spent on Investigation</label>
            <input
              type="number"
              name="tfmInvestigationHours"
              value={formData.tfmInvestigationHours}
              onChange={handleInputChange}
              className="modern-input"
              placeholder="e.g., 2.5"
              step="0.25"
              min="0"
              max="24"
              style={{ maxWidth: '200px' }}
            />
          </div>

          <div className="formGroup">
            <label>Notes</label>
            <textarea
              name="tfmNotes"
              value={formData.tfmNotes}
              onChange={handleInputChange}
              className="modern-input"
              rows="4"
              placeholder="Enter any additional notes for the TFM Arrest Sheet"
            />
          </div>
        </>
      )}

      <div style={{ display: 'flex', gap: '10px' }}>
        <button type="submit" className="modern-btn" disabled={loading}>
          {loading ? (isEditing ? 'Updating...' : 'Adding...') : (isEditing ? 'Update Record' : 'Add Record')}
        </button>
        {isEditing && (
          <button
            type="button"
            className="modern-btn"
            onClick={onCancelEdit}
            style={{ backgroundColor: '#f44336' }}
          >
            Cancel
          </button>
        )}
      </div>
    </form>
  );
};

export default ImmigrantForm;
